import moment from 'moment'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import invoiceApi from 'services/api/invoices'

import { selectCurrentHotelId, selectIsInternalAdmin } from '../../accessors/index'
import { getUSDstring } from '../../utils/helpers'
import styles from './BillingRow/BillingRow.module.scss'
import { adminStatusNames, statusNames, statusStyles } from './statusNames'
const amountInfoStyles = {
  none: styles.amountInfo,
  good: styles.amountInfoGood,
  exciting: styles.amountInfoExciting,
  badish: styles.amountInfoBadish,
  bad: styles.amountInfoBad
}

function getDateRange(date1, date2) {
  if (moment(date1).format('MMM YYYY') === moment(date2).format('MMM YYYY')) {
    return `${moment(date1).format('MMM YYYY')}`
  }

  if (moment(date1).format('YYYY') === moment(date2).format('YYYY')) {
    return `${moment(date1).format('MMM')} - ${moment(date2).format('MMM YYYY')}`
  }
  return `${moment(date1).format('MMM YYYY')} - ${moment(date2).format('MMM YYYY')}`
}

const InvoiceRow = ({
  subsidiaryInvoiceId,
  beginDate,
  endDate,
  totalAmountDue,
  totalAmountDueRemaining,
  status,
  deliveredOn,
  reconciliationDueDate,
  isPaymentInProgress,
  reconciliationPendingCount,
  isInvoicePdfGenerate,
  invoiceKind,
  generatedOn
}) => {
  const canBeReconcilied = moment().isBefore(moment(reconciliationDueDate)) // business logic

  const isDelivered = !!deliveredOn

  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const currentHotelId = useSelector(selectCurrentHotelId)

  const { addToast } = useToasts()

  useEffect(() => {
    ReactTooltip.rebuild()
  }, [isInternalAdmin])

  function regenerateInvoice(invoiceId) {
    addToast('Invoice is being regenerated', {
      appearance: 'info',
      autoDismiss: true
    })

    invoiceApi
      .regenerateInvoice({ invoiceId })
      .then(() => {
        addToast('Invoice has been regenerated', {
          appearance: 'success',
          autoDismiss: true
        })
      })
      .catch(() => {
        addToast('Invoice regeneration failed', {
          appearance: 'error',
          autoDismiss: true
        })
      })
  }

  function getAmount() {
    if (status === 'PaidPartially') {
      return (
        <span>
          {getUSDstring(totalAmountDueRemaining)}
          <del>{getUSDstring(totalAmountDue)}</del>{' '}
        </span>
      )
    }

    return getUSDstring(totalAmountDue)
  }

  const amount = getAmount()

  const title =
    moment().format('YYYY') === moment(generatedOn).format('YYYY')
      ? `${moment(generatedOn).format('MMMM')} invoice`
      : `${moment(generatedOn).format('MMMM YYYY')} invoice`

  const titleInfo = (
    <>
      {isInternalAdmin ? `#${subsidiaryInvoiceId} - ` : ''}
      {invoiceKind === 'FixedServices'
        ? `Subscription fee for ${getDateRange(beginDate, endDate)}`
        : invoiceKind === 'CustomCharges'
        ? `Additional fees for ${moment(beginDate).format('MMM YYYY')}`
        : `Billable messages from ${moment(beginDate).format('MMM YYYY')}`}
    </>
  )

  function getStatusName(status, canBeReconcilied, isInternalAdmin, isPaymentInProgress) {
    if (isPaymentInProgress) {
      return 'Payment in progress'
    }

    return isInternalAdmin ? adminStatusNames[status] : statusNames[status]
  }

  function getAmountStyle(status, canBeReconcilied, isPaymentInProgress) {
    if (isPaymentInProgress) {
      return statusStyles.Paid
    }

    if (
      (status === 'PaymentPending' || status === 'MonthInProgress') &&
      canBeReconcilied
    ) {
      return statusStyles.ReadyToReconcile
    }

    return statusStyles[status]
  }

  const amountInfo = getStatusName(
    status,
    canBeReconcilied,
    isInternalAdmin,
    isPaymentInProgress
  )

  const amountStyle = getAmountStyle(status, canBeReconcilied, isPaymentInProgress)

  const manageLink = `/manageBilling/${currentHotelId}/${subsidiaryInvoiceId}`

  if (isInternalAdmin) {
    return (
      <div
        className={[styles.rowBigger, styles.rowClickable].join(' ')}
        data-tip={
          deliveredOn
            ? `Delivered on ${moment(deliveredOn).format('MMM DD, YYYY')}`
            : 'Not delivered'
        }
      >
        <div className={styles.titleCol}>
          <div className={styles.title}>{title}</div>
          <div className={styles.titleInfo}>
            <span className={styles.titleInfoInner}>{titleInfo}</span>
          </div>
          <div className={styles.titleInfoHover}>
            <span className={styles.adminControls}>
              {isInvoicePdfGenerate && !isDelivered && (
                <Link to={`/invoice/${subsidiaryInvoiceId}`}>
                  <button>View final invoice</button>
                </Link>
              )}
              {isInvoicePdfGenerate && isDelivered && (
                <Link to={`/invoice/${subsidiaryInvoiceId}`}>
                  <button>View invoice</button>
                </Link>
              )}
              {!isInvoicePdfGenerate && (
                <Link to={`/invoice-preview/${subsidiaryInvoiceId}`}>
                  <button>Invoice preview</button>
                </Link>
              )}
              {manageLink && status !== 'MonthSkipped' && (
                <Link to={manageLink}>
                  <button>Manage</button>
                </Link>
              )}
              {isInvoicePdfGenerate && !isDelivered && (
                <button onClick={() => regenerateInvoice(subsidiaryInvoiceId)}>
                  Regenerate invoice
                </button>
              )}
            </span>
          </div>
        </div>
        <div className={styles.amountCol}>
          <div className={styles.amount}>{amount}</div>
          {reconciliationPendingCount > 0 ? (
            <Link to={manageLink} className={[amountInfoStyles.bad]}>
              <span className={styles.pill}>{reconciliationPendingCount}</span> Pending
              reconciliation
            </Link>
          ) : (
            <div className={[amountInfoStyles[amountStyle]]}>{amountInfo}</div>
          )}{' '}
        </div>
      </div>
    )
  }

  if (!isInvoicePdfGenerate) {
    return (
      <Link
        className={[styles.rowBigger, styles.rowClickable].join(' ')}
        to={`/invoice-preview/${subsidiaryInvoiceId}`}
      >
        <div className={styles.titleCol}>
          <div className={styles.title}>{title}</div>
          <div className={styles.titleInfo}>
            <span className={styles.titleInfoInner}>{titleInfo}</span>
          </div>
          <div className={styles.titleInfoHover}>View invoice →</div>
        </div>
        <div className={styles.amountCol}>
          <div className={styles.amount}>{amount}</div>
          <div className={[amountInfoStyles[amountStyle]]}>{amountInfo}</div>
        </div>
      </Link>
    )
  }

  return (
    <Link
      className={[styles.rowBigger, styles.rowClickable].join(' ')}
      to={`/invoice/${subsidiaryInvoiceId}`}
    >
      <div className={styles.titleCol}>
        <div className={styles.title}>{title}</div>
        <div className={styles.titleInfo}>
          <span className={styles.titleInfoInner}>{titleInfo}</span>
        </div>
        <div className={styles.titleInfoHover}>View invoice →</div>
      </div>
      <div className={styles.amountCol}>
        <div className={styles.amount}>{amount}</div>
        <div className={[amountInfoStyles[amountStyle]]}>{amountInfo}</div>
      </div>
    </Link>
  )
}

export default InvoiceRow
